/**
 * redux-persist storage engine backed by localStorage.
 *
 * Replaces `redux-persist/lib/storage`, whose CommonJS default export is
 * mis-unwrapped by Vite 8's Rolldown bundler (`storage.getItem is not a
 * function` at runtime).
 *
 * Two behaviours of the module it replaces have to be preserved:
 *
 * - redux-persist calls `storage.getItem(key).then(...)` unconditionally, so
 *   every method must return a promise even though localStorage is synchronous.
 * - localStorage is not always reachable. It is absent outside a browser, and
 *   merely *accessing* `window.localStorage` throws when a browser is set to
 *   block site data. Writes are also queued on a timer, so one can land after
 *   the page (or, in tests, the jsdom environment) is gone. Persistence is
 *   skipped in those cases rather than throwing, matching what redux-persist's
 *   own createWebStorage does with its noop fallback.
 */

/** localStorage, or null when it cannot be reached. */
function getStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    // Accessing localStorage throws when the browser blocks site data.
    return null
  }
}

const localStorageEngine = {
  getItem: async (key) => getStorage()?.getItem(key) ?? null,
  setItem: async (key, value) => {
    getStorage()?.setItem(key, value)
  },
  removeItem: async (key) => {
    getStorage()?.removeItem(key)
  },
}

export default localStorageEngine
